import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Button, Table, Spin, message } from 'antd';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import pdfMake from 'pdfmake/build/pdfmake';

import type { ColumnsType } from 'antd/es/table';


export interface AttendanceTheoryRow {
  LopHocPhanID: string;
  TenLopHP: string;
  TenBaiGiang: string;
  GiangVien: string;
  NgayGiang: string;
  BuoiGiang: string;
  BuoiSo: number;
  SoTietLT: number;
  SoTietTH: number;
  SiSo: number;
}

// Đặt columns thành một hàm nhận data: AttendanceTheoryRow[]
function getColumns(data: AttendanceTheoryRow[]): ColumnsType<AttendanceTheoryRow> {
  return [
    {
      title: 'Lớp học phần',
      dataIndex: 'TenLopHP',
      key: 'TenLopHP',
      sorter: (a: AttendanceTheoryRow, b: AttendanceTheoryRow) => (a.TenLopHP || '').localeCompare(b.TenLopHP || ''),
      filters: Array.from(new Set(data.map((row: AttendanceTheoryRow) => row.TenLopHP))).filter(Boolean).map((name) => ({ text: name, value: name })),
      onFilter: (value: any, record: AttendanceTheoryRow) => record.TenLopHP === value,
    },
    {
      title: 'Tên bài giảng',
      dataIndex: 'TenBaiGiang',
      key: 'TenBaiGiang',
      sorter: (a: AttendanceTheoryRow, b: AttendanceTheoryRow) => (a.TenBaiGiang || '').localeCompare(b.TenBaiGiang || ''),
      filters: Array.from(new Set(data.map((row: AttendanceTheoryRow) => row.TenBaiGiang))).filter(Boolean).map((name) => ({ text: name, value: name })),
      onFilter: (value: any, record: AttendanceTheoryRow) => record.TenBaiGiang === value,
    },
    {
      title: 'Giảng viên',
      dataIndex: 'GiangVien',
      key: 'GiangVien',
      sorter: (a: AttendanceTheoryRow, b: AttendanceTheoryRow) => (a.GiangVien || '').localeCompare(b.GiangVien || ''),
      filters: Array.from(new Set(data.map((row: AttendanceTheoryRow) => row.GiangVien))).filter(Boolean).map((name) => ({ text: name, value: name })),
      onFilter: (value: any, record: AttendanceTheoryRow) => record.GiangVien === value,
    },
    {
      title: 'Ngày giảng',
      dataIndex: 'NgayGiang',
      key: 'NgayGiang',
      sorter: (a: AttendanceTheoryRow, b: AttendanceTheoryRow) => new Date(a.NgayGiang).getTime() - new Date(b.NgayGiang).getTime(),
      render: (val: string) => val ? dayjs(val).format('DD/MM/YYYY') : '',
    },
    { title: 'Buổi giảng', dataIndex: 'BuoiGiang', key: 'BuoiGiang' },
    { title: 'Buổi số', dataIndex: 'BuoiSo', key: 'BuoiSo' },
    { title: 'Số tiết LT', dataIndex: 'SoTietLT', key: 'SoTietLT' },
    { title: 'Số tiết TH', dataIndex: 'SoTietTH', key: 'SoTietTH' },
    { title: 'Sĩ số', dataIndex: 'SiSo', key: 'SiSo' },
  ];
}

export default function TeachingHoursStatisticPage() {
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string | undefined>();
  const [selectedSubject, setSelectedSubject] = useState<string | undefined>();
  const [data, setData] = useState<AttendanceTheoryRow[]>([]);
  const [displayedData, setDisplayedData] = useState<AttendanceTheoryRow[]>([]);

  useEffect(() => {
    fetch('/api/v1/courses', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data?.data)) setCourses(data.data);
        else setCourses([]);
      });
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetch(`/api/v1/subjects/by-khoahoc/${selectedCourse}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          if (Array.isArray(data)) setSubjects(data);
          else if (data && Array.isArray(data.data)) setSubjects(data.data);
          else setSubjects([]);
        });
    } else {
      setSubjects([]);
    }
  }, [selectedCourse]);

  useEffect(() => {
    setDisplayedData(data);
  }, [data]);

  const handleStatistic = async () => {
    if (!selectedCourse || !selectedSubject) {
      message.warning('Vui lòng chọn đủ Khóa học và Môn học!');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('courseId', selectedCourse);
      params.append('subjectId', selectedSubject);
      const res = await fetch(`/api/v1/attendance-theory/statistics?${params.toString()}`, { credentials: 'include' });
      const json = await res.json();
      if (json && json.error) {
        message.error('Lỗi backend: ' + json.error);
        setData([]);
      } else if (Array.isArray(json)) {
        setData(json);
      } else {
        setData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!data.length) {
      message.warning('Không có dữ liệu để in!');
      return;
    }
    const tenMonHoc = selectedSubject ? (subjects.find(s => s.MonHocID === selectedSubject)?.TenMonHoc || '') : '';
    const tenKhoaHoc = selectedCourse ? (courses.find(c => c.KhoaHocID === selectedCourse)?.TenKhoaHoc || '') : '';
    const wsData = [
      ['UBND TỈNH THÁI BÌNH', '', '', '', '', '', '', '', '', ''],
      ['TRƯỜNG CAO ĐẲNG Y TẾ', '', '', '', '', '', '', '', '', ''],
      ['THỐNG KÊ SỐ BUỔI GIẢNG THEO MÔN HỌC', '', '', '', '', '', '', '', '', ''],
      [`Khóa học: ${tenKhoaHoc}`, '', '', '', '', `Môn học: ${tenMonHoc}`, '', '', '', ''],
      []
    ];
    wsData.push([
      'Lớp học phần', 'Tên bài giảng', 'Giảng viên', 'Ngày giảng', 'Buổi', 'Số tiết LT', 'Số tiết TH', 'Buổi số', 'Sĩ số'
    ]);
    wsData.push(...data.map(row => [
      String(row.TenLopHP),
      String(row.TenBaiGiang),
      String(row.GiangVien),
      row.NgayGiang ? dayjs(row.NgayGiang).format('DD/MM/YYYY') : '',
      String(row.BuoiGiang),
      String(row.SoTietLT),
      String(row.SoTietTH),
      String(row.BuoiSo),
      String(row.SiSo)
    ]));
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!merges'] = [
      { s: { r:0, c:0 }, e: { r:0, c:9 } },
      { s: { r:1, c:0 }, e: { r:1, c:9 } },
      { s: { r:2, c:0 }, e: { r:2, c:9 } },
      { s: { r:3, c:0 }, e: { r:3, c:4 } },
      { s: { r:3, c:5 }, e: { r:3, c:6 } }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Thống kê giờ giảng');
    XLSX.writeFile(wb, 'ThongKeGioGiang.xlsx');
  };

  const handleExportPDF = () => {
    if (!displayedData.length) {
      message.warning('Không có dữ liệu để in!');
      return;
    }
    const tenMonHoc = selectedSubject ? subjects.find(s => s.MonHocID === selectedSubject)?.TenMonHoc || '' : '';
    const tenKhoaHoc = selectedCourse ? courses.find(c => c.KhoaHocID === selectedCourse)?.TenKhoaHoc || '' : '';
    const tongSoTietLT = displayedData.reduce((total: number, row: AttendanceTheoryRow) => total + (parseInt(row.SoTietLT as any) || 0), 0);
    const tongSoTietTH = displayedData.reduce((total: number, row: AttendanceTheoryRow) => total + (parseInt(row.SoTietTH as any) || 0), 0);
    const tongSoBuoi = tongSoTietLT + tongSoTietTH;
    const now = new Date();
    const todayStr = `Thái Bình, ngày ${now.getDate()} tháng ${now.getMonth() + 1} năm ${now.getFullYear()}`;

    // Table body
    const tableBody = [
      [
        { text: 'Lớp học phần', style: 'tableHeader' },
        { text: 'Tên bài giảng', style: 'tableHeader' },
        { text: 'Giảng viên', style: 'tableHeader' },
        { text: 'Ngày giảng', style: 'tableHeader' },
        { text: 'Buổi', style: 'tableHeader' },
        { text: 'Số tiết LT', style: 'tableHeader' },
        { text: 'Số tiết TH', style: 'tableHeader' },
        { text: 'Buổi số', style: 'tableHeader' },
        { text: 'Sĩ số', style: 'tableHeader' }
      ],
      ...displayedData.map((row: AttendanceTheoryRow) => [
        { text: row.TenLopHP || '', style: 'tableCell' },
        { text: row.TenBaiGiang || '', style: 'tableCell', alignment: 'left' },
        { text: row.GiangVien || '', style: 'tableCell' },
        { text: row.NgayGiang ? dayjs(row.NgayGiang).format('DD/MM/YYYY') : '', style: 'tableCell' },
        { text: row.BuoiGiang || '', style: 'tableCell' },
        { text: row.BuoiSo || '', style: 'tableCell', alignment: 'right' },
        { text: row.SoTietLT || '0', style: 'tableCell', alignment: 'right' },
        { text: row.SoTietTH || '0', style: 'tableCell', alignment: 'right' },
        { text: row.SiSo || '0', style: 'tableCell', alignment: 'right' }
      ]),
      [
        { text: 'Tổng cộng=', style: 'tableFooter', colSpan: 6, alignment: 'center' }, {}, {}, {}, {}, {},
        { text: tongSoTietLT.toString(), style: 'tableFooter', alignment: 'right' },
        { text: tongSoTietTH.toString(), style: 'tableFooter', alignment: 'right' },
        { text: '', style: 'tableFooter' }
      ]
    ];

    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [30, 20, 30, 30],
      content: [
        { text: 'UBND TỈNH THÁI BÌNH', style: 'headerLeft' },
        { text: 'TRƯỜNG CAO ĐẲNG Y TẾ', style: 'headerLeft', margin: [0, 0, 0, 8] },
        { text: 'THỐNG KÊ SỐ BUỔI GIẢNG THEO MÔN HỌC', style: 'mainTitle' },
        {
          columns: [
            { width: '60%', text: [
              { text: 'Họ và tên GV: ', bold: true }, '....................................................', '\n',
              { text: 'Khóa học: ', bold: true }, tenKhoaHoc, '    ',
              { text: 'Môn học: ', bold: true }, tenMonHoc
            ], style: 'infoLine' },
            { width: '40%', text: [
              { text: 'Số tiết LT: ', bold: true }, tongSoTietLT.toString(), '    ',
              { text: 'Số tiết TH: ', bold: true }, tongSoTietTH.toString(), '\n',
              { text: 'Tổng số tiết ', bold: true }, tongSoBuoi.toString()
            ], style: 'infoLine' }
          ],
          columnGap: 10,
          margin: [0, 0, 0, 8]
        },
        {
          style: 'tableRow',
          table: {
            headerRows: 1,
            widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: tableBody
          },
          layout: {
            fillColor: (rowIndex: number) => rowIndex === 0 ? '#e6f7ff' : null,
            hLineWidth: () => 0.8,
            vLineWidth: () => 0.8,
            hLineColor: () => '#444',
            vLineColor: () => '#444',
          },
          margin: [0, 8, 0, 8]
        },
        { text: todayStr, alignment: 'right', margin: [0, 30, 30, 0], italics: true },
        {
          columns: [
            { width: '33%', text: 'Phòng QLĐT', alignment: 'center', margin: [0, 8, 0, 0] },
            { width: '33%', text: 'Trưởng Khoa/Bộ môn', alignment: 'center', margin: [0, 8, 0, 0] },
            { width: '33%', text: 'Giảng viên chủ giảng', alignment: 'center', margin: [0, 8, 0, 0] }
          ]
        }
      ],
      styles: {
        headerLeft: { fontSize: 11, bold: true, alignment: 'left' },
        mainTitle: { fontSize: 18, bold: true, alignment: 'center', margin: [0, 0, 0, 12] },
        infoLine: { fontSize: 12, margin: [0, 2, 0, 2] },
        tableHeader: { fillColor: '#e6f7ff', bold: true, fontSize: 10, color: '#005baa', alignment: 'center', margin: [0, 6, 0, 6] },
        tableCell: { fontSize: 10, alignment: 'center' },
        tableFooter: { fontSize: 10, bold: true, alignment: 'center' },
        tableRow: { fontSize: 10 },
      },
      defaultStyle: {
        font: 'Roboto',
        fontSize: 11
      }
    };
    pdfMake.createPdf(docDefinition).open();
  };

  return (
    <Card title="Thống kê giờ giảng theo môn học">
      <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
        <Col span={12}>
          <Select style={{ width: '100%' }} placeholder="Chọn khóa học" value={selectedCourse} onChange={v => { setSelectedCourse(v); setSelectedSubject(undefined); }} options={(Array.isArray(courses) ? courses : []).map(c => ({ label: `${c.KhoaHocID} - ${c.TenKhoaHoc}`, value: c.KhoaHocID }))} allowClear />
        </Col>
        <Col span={12}>
          <Select style={{ width: '100%' }} placeholder="Chọn môn học" value={selectedSubject} onChange={v => setSelectedSubject(v)} options={subjects.map(s => ({ label: `${s.MonHocID} - ${s.TenMonHoc}`, value: s.MonHocID }))} allowClear />
        </Col>
      </Row>
      
      <Button type="primary" onClick={handleStatistic} loading={loading}>Thống kê theo Khóa học, Môn học</Button>
      <Button onClick={handleExportExcel} style={{marginLeft:8}}>In thống kê (Excel)</Button>
      <Button onClick={handleExportPDF} style={{marginLeft:8}}>In thống kê (PDF)</Button>
      <Row gutter={16} style={{ marginBottom: 16 }} align="middle"></Row>
      <Spin spinning={loading}>
        <Table
          columns={getColumns(data)}
          dataSource={data}
          onChange={(_, __, ___, { currentDataSource }) => setDisplayedData(currentDataSource)}
          rowKey={r => r.LopHocPhanID + r.BuoiSo + r.NgayGiang}
          pagination={false}
          summary={pageData => {
            let totalLT = 0, totalTH = 0;
            pageData.forEach(row => {
              totalLT += Number(row.SoTietLT) || 0;
              totalTH += Number(row.SoTietTH) || 0;
            });
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>Tổng cộng</Table.Summary.Cell>
                <Table.Summary.Cell index={6}>{totalLT}</Table.Summary.Cell>
                <Table.Summary.Cell index={7}>{totalTH}</Table.Summary.Cell>
                <Table.Summary.Cell index={8}></Table.Summary.Cell>
                <Table.Summary.Cell index={9}></Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: 'No data' }}
        />
      </Spin>
    </Card>
  );
} 